import { useGame, ITEM_NAMES } from '../state/useGame';
import type { ItemStack, SlotRef } from '../domain/economy/containers';

type Props = {
  slotRef: SlotRef;
  stack: ItemStack | null;
  /** Position label, e.g. "Bank tab 2 slot 5" or "Inventory slot 3". */
  label: string;
};

/**
 * One container slot. Every interaction path — click, keyboard (native
 * button activation), drag/drop — routes into the same store actions and
 * therefore the same pure transfer engine. Empty slots stay focusable so
 * they can be chosen as destinations.
 */
export function ItemSlot({ slotRef, stack, label }: Props) {
  const selected = useGame((s) => s.selected);
  const activateSlot = useGame((s) => s.activateSlot);
  const transferSlot = useGame((s) => s.transferSlot);
  const isSelected =
    selected !== null && selected.container === slotRef.container && selected.index === slotRef.index;

  return (
    <button
      className={`bank-slot ${stack ? '' : 'empty'} ${isSelected ? 'selected' : ''}`}
      aria-label={`${label}: ${stack ? `${ITEM_NAMES[stack.itemId]} ×${stack.qty}` : 'empty'}`}
      aria-pressed={isSelected}
      draggable={!!stack}
      onDragStart={(e) => {
        if (!stack) return;
        e.dataTransfer.setData('text/hermanity-idle', JSON.stringify(slotRef));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDrop={(e) => {
        const raw = e.dataTransfer.getData('text/hermanity-idle');
        if (!raw) return;
        e.preventDefault();
        try {
          const from = JSON.parse(raw) as SlotRef;
          const source = useGame.getState().state[from.container][from.index];
          if (source) transferSlot(from, slotRef, source.qty);
        } catch {
          // Malformed foreign drag payload: ignore.
        }
      }}
      onClick={() => activateSlot(slotRef)}
    >
      {stack ? (
        <>
          <span className="qty">×{stack.qty}</span>
          {ITEM_NAMES[stack.itemId]}
        </>
      ) : null}
    </button>
  );
}
